import { getAnalytics } from "@tm/insurance/analytics";
import { CloseIcon } from "@tm/static/icons/CloseIcon";
import { ErrorIcon } from "@tm/static/icons/ErrorIcon";
import { FileIcon as FileIconSvg } from "@tm/static/icons/FileIcon";
import { UploadIcon } from "@tm/static/icons/UploadIcon";
import React, { useCallback, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  ExcelUploadError,
  ExcelUploadErrorCode,
  GENDER_VALUE_MAPPING,
  HEADER_MAPPING,
} from "./ExcelUpload.constants";
import {
  ErrorMessage,
  ExcelUploadWrapper,
  FileDetails,
  FileIcon,
  FileInfo,
  FileName,
  FileSize,
  HiddenInput,
  LoadingSpinner,
  RemoveButton,
  UploadButton,
  UploadRow,
} from "./ExcelUpload.style";
import { ExcelUploadProps, TravellerData } from "./ExcelUpload.types";
import { formatFileSize, parseDateToYYYYMMDD } from "./ExcelUpload.utils";
import { useUploadTemplate } from "./utils";

const ExcelUpload: React.FC<ExcelUploadProps> = ({
  onDataParsed,
  onError,
  isExcelBypass = false,
  maxFileSizeMB = 5,
  maxRows = 100,
  buttonText = "Upload Excel",
  hideError = false,
  disabled = false,
  className,
  requiredFields,
  errorMessages,
  quoteId = "",
  itineraryId = "",
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string>("");
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const REQUIRED_FIELDS = requiredFields;
  const analytics = getAnalytics();

  // Hook for upload API call
  const {
    isLoading: isUploadLoading,
    error: uploadError,
    upload: uploadToServer,
  } = useUploadTemplate(quoteId, itineraryId, isExcelBypass);

  // Combined loading and error states
  const isLoading = isLocalLoading || isUploadLoading;
  const error = localError || uploadError || "";

  // Common file validation
  const validateFile = useCallback(
    (selectedFile: File): ExcelUploadError | null => {
      // Validate file size
      const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
      if (selectedFile.size > maxSizeBytes) {
        return new ExcelUploadError(ExcelUploadErrorCode.FILE_SIZE_EXCEEDED);
      }

      // Validate file type
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/x-ms-excel",
      ];
      const validExtensions = [".xlsx", ".xls"];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .slice(selectedFile.name.lastIndexOf("."));

      if (
        !validTypes.includes(selectedFile.type) &&
        !validExtensions.includes(fileExtension)
      ) {
        return new ExcelUploadError(ExcelUploadErrorCode.UNSUPPORTED_FILE_TYPE);
      }

      return null;
    },
    [maxFileSizeMB]
  );

  const validateHeaders = useCallback(
    (
      headers: string[]
    ): {
      headerMap: Map<number, keyof TravellerData>;
      missingFields: string[];
    } => {
      const headerMap = new Map<number, keyof TravellerData>();
      const foundFields = new Set<keyof TravellerData>();

      headers.forEach((header, index) => {
        const normalizedHeader = header?.toLowerCase().trim() || "";
        const mappedField = HEADER_MAPPING[normalizedHeader];

        if (mappedField && !foundFields.has(mappedField)) {
          headerMap.set(index, mappedField);
          foundFields.add(mappedField);
        }
      });

      // Check if all required fields are present
      const missingFields =
        REQUIRED_FIELDS?.filter((field) => !foundFields.has(field)) || [];

      return { headerMap, missingFields };
    },
    [REQUIRED_FIELDS]
  );

  // Upload file directly to server (bypass mode)
  const uploadFileToServer = useCallback(
    async (selectedFile: File) => {
      setIsLocalLoading(true);
      setLocalError("");

      try {
        // Validate file
        const validationError = validateFile(selectedFile);
        if (validationError) {
          throw validationError;
        }

        // Validate headers before uploading
        const arrayBuffer = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          throw new ExcelUploadError(ExcelUploadErrorCode.UNABLE_TO_READ_FILE);
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, {
          header: 1,
        });

        if (jsonData.length < 2) {
          throw new ExcelUploadError(ExcelUploadErrorCode.NO_DETAILS_FOUND);
        }

        // Parse and validate headers
        const headers = jsonData[0] as string[];
        const { missingFields } = validateHeaders(headers);

        if (missingFields.length > 0) {
          throw new ExcelUploadError(
            ExcelUploadErrorCode.FIELDS_MISSING,
            undefined,
            missingFields
          );
        }

        setIsLocalLoading(false);

        // Use the hook to upload the file
        const responseData = await uploadToServer(selectedFile);

        if (responseData) {
          setFile(selectedFile);

          // Route to payment link if it exists
          if (responseData.data?.checkoutUrl) {
            window.location.href = responseData.data.checkoutUrl;
            return;
          }
        }
      } catch (err) {
        if (err instanceof ExcelUploadError) {
          onError?.(err.code, err.missingFields);
          // Track excel error analytics
          analytics?.trackTravDetailsEvents({
            actionType: "error_shown",
            payload: `excel | ${err.message}`,
          });
        } else {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to upload Excel file";
          setLocalError(errorMessage);
          onError?.(ExcelUploadErrorCode.UNKNOWN_ERROR);
          // Track excel error analytics
          analytics?.trackTravDetailsEvents({
            actionType: "error_shown",
            payload: `excel | ${errorMessage}`,
          });
        }
        setIsLocalLoading(false);
      }
    },
    [validateFile, validateHeaders, onError, uploadToServer, analytics]
  );

  // Parse Excel file locally
  const parseExcelFile = useCallback(
    async (selectedFile: File) => {
      setIsLocalLoading(true);
      setLocalError("");

      try {
        // Validate file
        const validationError = validateFile(selectedFile);
        if (validationError) {
          throw validationError;
        }

        // Read file
        const arrayBuffer = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          throw new ExcelUploadError(ExcelUploadErrorCode.UNABLE_TO_READ_FILE);
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, {
          header: 1,
        });

        if (jsonData.length < 2) {
          throw new ExcelUploadError(ExcelUploadErrorCode.NO_DETAILS_FOUND);
        }

        // Parse headers from first row
        const headers = jsonData[0] as string[];
        const { headerMap, missingFields } = validateHeaders(headers);

        if (missingFields.length > 0) {
          throw new ExcelUploadError(
            ExcelUploadErrorCode.FIELDS_MISSING,
            undefined,
            missingFields
          );
        }

        // Parse data rows
        const dataRows = jsonData.slice(1);
        if (dataRows.length > maxRows) {
          throw new ExcelUploadError(ExcelUploadErrorCode.TOO_MANY_ROWS);
        }

        const result: TravellerData[] = [];

        dataRows.forEach((row, rowIndex) => {
          // Skip completely empty rows
          if (!row || row.length === 0 || row.every((cell) => !cell)) {
            return;
          }

          const rowData: Partial<TravellerData> = {};
          let hasAnyData = false;

          headerMap.forEach((field, columnIndex) => {
            const cellValue = row[columnIndex];
            if (field === "dob") {
              // Parse DOB to yyyy-mm-dd format
              const parsedDob = parseDateToYYYYMMDD(cellValue);
              rowData[field] = parsedDob;
              if (parsedDob) hasAnyData = true;
            } else if (field === "gender") {
              // Normalize gender values (m/f/o, male/female/others -> MALE/FEMALE/OTHERS)
              const trimmedValue =
                cellValue !== undefined && cellValue !== null
                  ? String(cellValue).trim().toLowerCase()
                  : "";
              const normalizedGender =
                GENDER_VALUE_MAPPING[trimmedValue] ||
                trimmedValue.toUpperCase();
              rowData[field] = normalizedGender;
              if (normalizedGender) hasAnyData = true;
            } else {
              const trimmedValue =
                cellValue !== undefined && cellValue !== null
                  ? String(cellValue).trim()
                  : "";
              rowData[field] = trimmedValue;
              if (trimmedValue) hasAnyData = true;
            }
          });

          // Only add rows that have at least some data
          // Allow partial data - fields can be empty and will be filled by user
          if (hasAnyData) {
            result.push(rowData as TravellerData);
          }
        });

        if (result.length === 0) {
          throw new ExcelUploadError(ExcelUploadErrorCode.NO_DETAILS_FOUND);
        }

        // Deduplicate users based on passport number (keep first occurrence)
        // Empty passport numbers are allowed to have duplicates
        const seenPassports = new Set<string>();
        let duplicatesRemoved = 0;
        const uniqueResult = result.filter((traveller) => {
          const passport = traveller.passport?.trim() || "";
          // Allow empty passports (don't filter them out)
          if (!passport) {
            return true;
          }
          // For non-empty passports, only keep the first occurrence
          if (seenPassports.has(passport)) {
            duplicatesRemoved++;
            return false;
          }
          seenPassports.add(passport);
          return true;
        });

        onDataParsed?.(uniqueResult, duplicatesRemoved, selectedFile);
        setFile(selectedFile);
      } catch (err) {
        if (err instanceof ExcelUploadError) {
          onError?.(err.code, err.missingFields);
          // Track excel error analytics
          analytics?.trackTravDetailsEvents({
            actionType: "error_shown",
            payload: `excel | ${err.message}`,
          });
        } else {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to parse Excel file";
          setLocalError(errorMessage);
          onError?.(ExcelUploadErrorCode.UNKNOWN_ERROR);
          // Track excel error analytics
          analytics?.trackTravDetailsEvents({
            actionType: "error_shown",
            payload: `excel | ${errorMessage}`,
          });
        }
      } finally {
        setIsLocalLoading(false);
      }
    },
    [validateFile, maxRows, onDataParsed, onError, validateHeaders, analytics]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        if (isExcelBypass) {
          uploadFileToServer(selectedFile);
        } else {
          parseExcelFile(selectedFile);
        }
      }
      // Reset input value to allow re-selecting same file
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [isExcelBypass, uploadFileToServer, parseExcelFile]
  );

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setLocalError("");
  }, []);

  const handleUploadClick = useCallback(() => {
    if (!disabled && !isLoading) {
      inputRef.current?.click();
    }
  }, [disabled, isLoading]);

  return (
    <ExcelUploadWrapper className={className}>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileSelect}
        disabled={disabled}
      />

      {!file && (
        <UploadRow>
          <UploadButton
            type="button"
            disabled={disabled}
            isLoading={isLoading}
            onClick={handleUploadClick}
          >
            {isLoading ? (
              <>
                <LoadingSpinner />
                {isExcelBypass ? "Uploading..." : "Processing..."}
              </>
            ) : (
              <>
                <UploadIcon />
                {buttonText}
              </>
            )}
          </UploadButton>
          {error && !hideError && (
            <ErrorMessage>
              <ErrorIcon />
              <span>{error}</span>
            </ErrorMessage>
          )}
        </UploadRow>
      )}

      {file && (
        <FileInfo>
          <FileDetails>
            <FileIcon>
              <FileIconSvg />
            </FileIcon>
            <FileName>{file.name}</FileName>
            <FileSize>{formatFileSize(file.size)}</FileSize>
          </FileDetails>
          <RemoveButton onClick={handleRemoveFile} title="Remove file">
            <CloseIcon />
          </RemoveButton>
        </FileInfo>
      )}
    </ExcelUploadWrapper>
  );
};

export default ExcelUpload;
